import React, { useState, useEffect, useCallback } from 'react';
import api, { extractApiError } from '../../services/api';
import toast from 'react-hot-toast';

// Formulário especializado para a transportadora "Frete por Distância (Mapbox)":
// exibe as configurações já cadastradas (endereço de origem, perfil de rota),
// mas nunca mostra o valor do Mapbox Access Token — apenas indica se já está
// configurado e permite substituí-lo, deixando o campo em branco para preservar
// o token salvo anteriormente (merge feito no backend em freightService).
export default function DistanciaCredentialForm({ carrierId, fields, onSave, saving, accentColor }) {
  const [values, setValues] = useState({});
  const [hasToken, setHasToken] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});

  const accent = accentColor || '#0891b2';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/freight/carriers/${carrierId}`);
      const credentials = data.data?.config?.credentials || {};
      const prefill = {};
      fields.forEach((f) => {
        if (f.type === 'password') return;
        if (credentials[f.key] != null) prefill[f.key] = credentials[f.key];
      });
      setValues(prefill);
      setHasToken(!!credentials.mapbox_token);
    } catch (err) {
      toast.error(extractApiError(err, 'Erro ao carregar configuração salva'));
    } finally {
      setLoading(false);
    }
  }, [carrierId, fields]);

  useEffect(() => { load(); }, [load]);

  function set(key, value) {
    setValues((v) => ({ ...v, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  }

  function validate() {
    const errs = {};
    fields.forEach((f) => {
      if (f.key === 'mapbox_token') {
        if (f.required && !hasToken && !values.mapbox_token?.trim()) {
          errs.mapbox_token = 'Mapbox Access Token é obrigatório';
        }
        return;
      }
      if (f.required && !values[f.key]?.trim()) {
        errs[f.key] = `${f.label} é obrigatório`;
      }
    });
    return errs;
  }

  function handleSubmit(e) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    onSave(values);
  }

  if (loading) {
    return <p style={{ color: '#9ca3af', fontSize: '13px' }}>Carregando configuração salva...</p>;
  }

  return (
    <div style={S.wrap}>
      <div style={S.infoBox}>
        <span style={S.infoIcon}>ℹ️</span>
        <p style={S.infoText}>
          As configurações já cadastradas são exibidas abaixo, com exceção do Mapbox
          Access Token, que nunca é exibido por segurança. Deixe o token em branco
          para manter o valor salvo anteriormente.
        </p>
      </div>

      <form onSubmit={handleSubmit} noValidate style={S.form}>
        {fields.map((field) => {
          const isToken = field.key === 'mapbox_token';
          const hasError = !!errors[field.key];

          return (
            <div key={field.key} style={S.field}>
              <div style={S.labelRow}>
                <label style={S.label}>
                  {field.label}
                  {field.required && <span style={S.required}> *</span>}
                </label>
                {isToken && hasToken && (
                  <span style={S.savedBadge}>🔒 Token já configurado</span>
                )}
              </div>

              <input
                type={isToken ? 'password' : 'text'}
                value={values[field.key] || ''}
                onChange={(e) => set(field.key, e.target.value)}
                placeholder={isToken
                  ? (hasToken ? 'Deixe em branco para manter o token salvo' : field.placeholder || '')
                  : (field.placeholder || '')}
                style={{ ...S.input, ...(hasError ? S.inputError : {}) }}
                autoComplete={isToken ? 'new-password' : 'off'}
              />

              {field.helpText && !hasError && (
                <span style={S.helpText}>{field.helpText}</span>
              )}
              {hasError && (
                <span style={S.errorText}>{errors[field.key]}</span>
              )}
            </div>
          );
        })}

        <div style={S.footer}>
          <div style={S.footerInfo}>
            <span style={S.lockIcon}>🔒</span>
            <span style={S.footerText}>O Mapbox Access Token nunca é exibido em texto puro</span>
          </div>
          <button
            type="submit"
            disabled={saving}
            style={{ ...S.btnSave, background: accent }}
          >
            {saving ? 'Salvando...' : '💾 Salvar configuração'}
          </button>
        </div>
      </form>
    </div>
  );
}

const S = {
  wrap: {},
  infoBox: {
    display: 'flex', gap: '10px', alignItems: 'flex-start',
    background: '#ecfeff', border: '1px solid #a5f3fc',
    borderRadius: '8px', padding: '12px 14px', marginBottom: '20px',
  },
  infoIcon: { fontSize: '16px', flexShrink: 0, marginTop: '1px' },
  infoText: { fontSize: '13px', color: '#0e7490', lineHeight: 1.5, margin: 0 },
  form: { display: 'flex', flexDirection: 'column', gap: '18px' },
  field: { display: 'flex', flexDirection: 'column', gap: '5px' },
  labelRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  label: { fontSize: '13px', fontWeight: '600', color: '#374151' },
  required: { color: '#dc2626' },
  savedBadge: { fontSize: '11px', color: '#0891b2', fontWeight: '600' },
  input: {
    padding: '10px 14px',
    border: '1.5px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '14px',
    fontFamily: 'inherit',
    color: '#111827',
    background: '#f9fafb',
    outline: 'none',
    transition: 'border-color 0.15s',
  },
  inputError: { borderColor: '#dc2626', background: '#fef2f2' },
  helpText: { fontSize: '12px', color: '#9ca3af', lineHeight: 1.4 },
  errorText: { fontSize: '12px', color: '#dc2626', fontWeight: '500' },
  footer: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    paddingTop: '16px', borderTop: '1px solid #f3f4f6',
    flexWrap: 'wrap', gap: '12px',
    marginTop: '4px',
  },
  footerInfo: { display: 'flex', alignItems: 'center', gap: '6px' },
  lockIcon: { fontSize: '14px' },
  footerText: { fontSize: '12px', color: '#9ca3af' },
  btnSave: {
    padding: '10px 22px', color: '#fff',
    border: 'none', borderRadius: '8px',
    fontSize: '14px', fontWeight: '600',
    cursor: 'pointer', fontFamily: 'inherit',
    whiteSpace: 'nowrap',
  },
};
